import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  KeyIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import Card, { CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Avatar from '@/components/ui/Avatar';
import Modal, { ModalBody, ModalFooter } from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import {
  TableContainer, Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell,
} from '@/components/ui/Table';
import { useApi } from '@/hooks/useApi';
import { apiClient, getErrorMessage } from '@/lib/api';
import type { AdminUser, UsersListResponse, OrganizationsListResponse } from '@/types/api';

const roleColors: Record<string, 'primary' | 'secondary' | 'slate'> = {
  admin: 'primary',
  'k-admin': 'primary',
  'k-csteam': 'primary',
  client: 'secondary',
  'c-owner': 'secondary',
  'c-client': 'secondary',
  'c-reader': 'secondary',
  service: 'slate',
};

const initialFormState = {
  email: '',
  password: '',
  role: 'client' as 'admin' | 'client' | 'service',
  organization_id: '' as string | null,
};

const toEditableRole = (role: string): 'admin' | 'client' | 'service' => {
  if (role === 'admin' || role === 'k-admin' || role === 'k-csteam') return 'admin';
  if (role === 'client' || role === 'c-owner' || role === 'c-client' || role === 'c-reader') return 'client';
  return 'service';
};

export default function AdminUsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [roleFilter, setRoleFilter] = useState(searchParams.get('role') || '');
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [form, setForm] = useState(initialFormState);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState(initialFormState);
  const [editError, setEditError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Build API endpoint with query params
  const apiEndpoint = useMemo(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (roleFilter) params.set('role', roleFilter);
    params.set('pageSize', '50');
    const queryString = params.toString();
    return `admin/users${queryString ? `?${queryString}` : ''}`;
  }, [debouncedSearch, roleFilter]);

  // Fetch users from API
  const { data, isLoading, error, refetch } = useApi<UsersListResponse>(apiEndpoint);

  // Fetch organizations for create-user dropdown
  const { data: orgsData } = useApi<OrganizationsListResponse>('admin/organizations?pageSize=100');
  const organizations = orgsData?.items || [];

  const handleCreateUser = useCallback(async () => {
    const email = form.email.trim();
    if (!email) {
      setCreateError('Email is required');
      return;
    }
    if (!form.role) {
      setCreateError('Role is required');
      return;
    }
    if (!form.password || form.password.length < 6) {
      setCreateError('Password must be at least 6 characters');
      return;
    }
    setCreateError(null);
    setIsCreating(true);
    try {
      await apiClient.post('admin/users', {
        email,
        role: form.role,
        organization_id: form.organization_id || null,
        password: form.password,
      });
      setForm(initialFormState);
      setShowModal(false);
      refetch();
    } catch (err) {
      setCreateError(getErrorMessage(err));
    } finally {
      setIsCreating(false);
    }
  }, [form, refetch]);

  const openModal = useCallback(() => {
    setForm(initialFormState);
    setCreateError(null);
    setShowModal(true);
  }, []);

  const openEditModal = useCallback((user: AdminUser) => {
    setEditingUser(user);
    setEditForm({
      email: user.email,
      password: '',
      role: toEditableRole(user.role),
      organization_id: user.organization_id || null,
    });
    setEditError(null);
    setShowEditModal(true);
  }, []);

  const handleUpdateUser = useCallback(async () => {
    if (!editingUser) return;
    const email = editForm.email.trim();
    if (!email) {
      setEditError('Email is required');
      return;
    }
    if (!editForm.role) {
      setEditError('Role is required');
      return;
    }
    if (editForm.password && editForm.password.length < 6) {
      setEditError('Password must be at least 6 characters');
      return;
    }
    setEditError(null);
    setIsUpdating(true);
    try {
      await apiClient.patch(`admin/users/${editingUser.id}`, {
        email,
        role: editForm.role,
        organization_id: editForm.organization_id || null,
        ...(editForm.password ? { password: editForm.password } : {}),
      });
      setShowEditModal(false);
      setEditingUser(null);
      setEditForm(initialFormState);
      refetch();
    } catch (err) {
      setEditError(getErrorMessage(err));
    } finally {
      setIsUpdating(false);
    }
  }, [editingUser, editForm, refetch]);

  // Update URL search params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (roleFilter) params.set('role', roleFilter);
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, roleFilter, setSearchParams]);

  const users = data?.items || [];
  const total = data?.total || 0;

  // Calculate stats from actual data
  const stats = useMemo(() => {
    const adminCount = users.filter(u => u.role === 'admin' || u.role === 'k-admin' || u.role === 'k-csteam').length;
    const clientCount = users.filter(u => u.role === 'client' || u.role === 'c-owner' || u.role === 'c-client' || u.role === 'c-reader').length;
    const activeCount = users.filter(u => u.status === 'active').length;
    return {
      total,
      admins: adminCount,
      clients: clientCount,
      active: activeCount,
    };
  }, [users, total]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">User Management</h1>
          <p className="text-slate-600 dark:text-slate-300 mt-1">
            Manage users and their permissions
            {total > 0 && <span className="ml-2 text-slate-500 dark:text-slate-400">({total} users)</span>}
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className="w-4 h-4" />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button leftIcon={<PlusIcon className="w-4 h-4" />} onClick={openModal}>
            Add User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardBody className="text-center">
          <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.total}</p>
          <p className="text-sm text-slate-600 dark:text-slate-300">Total Users</p>
        </CardBody></Card>
        <Card><CardBody className="text-center">
          <p className="text-3xl font-bold text-primary-500">{stats.admins}</p>
          <p className="text-sm text-slate-600 dark:text-slate-300">Admins</p>
        </CardBody></Card>
        <Card><CardBody className="text-center">
          <p className="text-3xl font-bold text-secondary-500">{stats.clients}</p>
          <p className="text-sm text-slate-600 dark:text-slate-300">Clients</p>
        </CardBody></Card>
        <Card><CardBody className="text-center">
          <p className="text-3xl font-bold text-success-500">{stats.active}</p>
          <p className="text-sm text-slate-600 dark:text-slate-300">Active</p>
        </CardBody></Card>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search users by email or username..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          <Select
            options={[
              { value: '', label: 'All Roles' },
              { value: 'admin', label: 'Admin' },
              { value: 'client', label: 'Client' },
              { value: 'service', label: 'Service' },
            ]}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="w-40"
          />
        </div>
      </Card>

      {/* Error State */}
      {error && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Failed to load users</h3>
            <p className="text-slate-600 dark:text-slate-300 mb-4">{error}</p>
            <Button onClick={() => refetch()} leftIcon={<ArrowPathIcon className="w-4 h-4" />}>
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && !data && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner size="lg" />
            <p className="text-slate-600 dark:text-slate-300 mt-4">Loading users...</p>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !error && users.length === 0 && (
        <EmptyState
          icon={<UserGroupIcon className="w-12 h-12" />}
          title="No users found"
          description={
            debouncedSearch || roleFilter
              ? 'Try adjusting your search or filters'
              : 'Get started by adding your first user'
          }
          action={
            debouncedSearch || roleFilter ? (
              <Button variant="outline" onClick={() => { setSearch(''); setRoleFilter(''); }}>
                Clear Filters
              </Button>
            ) : (
              <Button
                leftIcon={<PlusIcon className="w-4 h-4" />}
                onClick={openModal}
              >
                Add User
              </Button>
            )
          }
        />
      )}

      {/* Users Table */}
      {!error && users.length > 0 && (
        <>
          <Card padding="none">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow isHoverable={false}>
                    <TableHeaderCell>User</TableHeaderCell>
                    <TableHeaderCell>Email</TableHeaderCell>
                    <TableHeaderCell>Role</TableHeaderCell>
                    <TableHeaderCell>Organization</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar name={user.username || user.email} size="sm" />
                          <span className="font-medium">{user.username || user.email.split('@')[0]}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-700 dark:text-slate-200">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={roleColors[user.role] || 'slate'}>{user.role}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-700 dark:text-slate-200">{user.organization_name || '--'}</TableCell>
                      <TableCell>
                        <Badge variant={user.status === 'active' ? 'success' : 'slate'}>{user.status || 'active'}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<PencilIcon className="w-3 h-3" />}
                            onClick={() => openEditModal(user)}
                          >
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" leftIcon={<KeyIcon className="w-3 h-3" />}>API Key</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Footer Info */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Showing {users.length} of {total} users
              {isLoading && <span className="ml-2">(refreshing...)</span>}
            </p>
          </div>
        </>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add User">
        <ModalBody className="space-y-4">
          {createError && (
            <div className="rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 px-4 py-3 text-sm text-danger-700 dark:text-danger-300">
              {createError}
            </div>
          )}
          <Input
            label="Email"
            type="email"
            placeholder="user@example.com"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
          <Input
            label="Password"
            type="password"
            placeholder="At least 6 characters"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          />
          <Select
            label="Role"
            options={[
              { value: 'client', label: 'Client' },
              { value: 'admin', label: 'Admin' },
              { value: 'service', label: 'Service' },
            ]}
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as 'admin' | 'client' | 'service' }))}
          />
          <Select
            label="Organization (optional)"
            options={[
              { value: '', label: 'None' },
              ...organizations.map((o) => ({ value: o.id, label: o.name })),
            ]}
            value={form.organization_id || ''}
            onChange={(e) => setForm((f) => ({ ...f, organization_id: e.target.value || null }))}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowModal(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreateUser} disabled={isCreating}>
            {isCreating ? 'Creating...' : 'Create User'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={() => {
          if (isUpdating) return;
          setShowEditModal(false);
          setEditingUser(null);
          setEditForm(initialFormState);
          setEditError(null);
        }}
        title={editingUser ? `Edit User: ${editingUser.username || editingUser.email}` : 'Edit User'}
      >
        <ModalBody className="space-y-4">
          {editError && (
            <div className="rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 px-4 py-3 text-sm text-danger-700 dark:text-danger-300">
              {editError}
            </div>
          )}
          <Input
            label="Email"
            type="email"
            placeholder="user@example.com"
            value={editForm.email}
            onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
          />
          <Input
            label="New Password (optional)"
            type="password"
            placeholder="Leave empty to keep current password"
            value={editForm.password}
            onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))}
          />
          <Select
            label="Role"
            options={[
              { value: 'client', label: 'Client' },
              { value: 'admin', label: 'Admin' },
              { value: 'service', label: 'Service' },
            ]}
            value={editForm.role}
            onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value as 'admin' | 'client' | 'service' }))}
          />
          <Select
            label="Organization (optional)"
            options={[
              { value: '', label: 'None' },
              ...organizations.map((o) => ({ value: o.id, label: o.name })),
            ]}
            value={editForm.organization_id || ''}
            onChange={(e) => setEditForm((f) => ({ ...f, organization_id: e.target.value || null }))}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setShowEditModal(false);
              setEditingUser(null);
              setEditForm(initialFormState);
              setEditError(null);
            }}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button onClick={handleUpdateUser} disabled={isUpdating}>
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
